import React from 'react';
import { ButtonV2, CopyButton, LinkIcon } from '@asigna/kit';
import { WalletWithIcons } from 'components';
import { PERMISSION_SIGNING_MESSAGE } from 'constants/base';
import { useReadOnlyWarning } from 'hooks';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import DeniedIcon from 'images/denied.svg';
import GrantedIcon from 'images/granted.svg';
import PendingIcon from 'images/pending_icon.svg';
import SignedIcon from 'images/signed_message_icon.svg';
import { usePermissionList } from 'store';
import { PermissionType } from 'types';
import { getAccountExplorerUrl, shortenAddress } from 'utils/helper';

import { TransactionCommonDialog } from '../TransactionDialog';

import { useHistoryDetailsDialogStyles } from './useHistoryDetailsDialogStyles';

interface TransactionCommonProps {
  messageId: string;
  isProcessing?: boolean;
  onClose: () => void;
  onSign: (message: string) => void;
}

export const MessageDetailsDialog = (props: TransactionCommonProps) => {
  const { messageId, onClose, onSign } = props;
  const { safe } = useSafe();
  const classes = useHistoryDetailsDialogStyles();
  const { permissionList } = usePermissionList();
  const walletAccount = useAddress() || '';
  const [network] = useNetwork();
  const readOnlyWarning = useReadOnlyWarning();

  const message = permissionList.find((message) => message._id === messageId);

  if (!message) return <></>;

  const isSigned =
    message?.signatures.some((signature) => signature.userAddress === walletAccount) ||
    message.signatures.length >= safe.threshold;

  const messageData =
    message.message.indexOf(PERMISSION_SIGNING_MESSAGE) !== -1
      ? JSON.parse(message.message.slice(30))
      : [];

  return (
    <TransactionCommonDialog
      open={Boolean(messageId)}
      title="Message details"
      button=""
      onClose={onClose}
      fromBottom
    >
      <div className={classes.wrapper}>
        <div className={classes.detailsWrapper}>
          <div className={classes.infoItem}>
            <p className="text-m14 text-regular mb-16">Message</p>
            {message.message.indexOf(PERMISSION_SIGNING_MESSAGE) !== -1 ? (
              <div className="mb-20">
                <span className="text-r12">{PERMISSION_SIGNING_MESSAGE}</span>
                <div className="grid grid-cols-[1fr_66px_58px_44px] text-m12 text-regular gap-4 mt-20">
                  <span>Address</span>
                  <span>
                    Execute
                    <br />
                    Txn
                  </span>
                  <span>
                    Create
                    <br />
                    Txn
                  </span>
                  <span>
                    Sign
                    <br />
                    Txn
                  </span>
                </div>
                {messageData.permissionsData.map(
                  (item: {
                    address: string;
                    processType: string;
                    permissions: PermissionType[];
                  }) => {
                    return (
                      <div
                        key={item.address}
                        className="grid grid-cols-[1fr_66px_58px_44px] text-m12 items-center mt-16 gap-4"
                      >
                        <div className="flex items-center gap-4 sm:gap-8 mr-16">
                          {shortenAddress(item.address)}
                          <CopyButton copy={item.address} />
                          <LinkIcon href={getAccountExplorerUrl(item.address, network)} />
                        </div>
                        <img
                          src={item.permissions.includes('EXECUTE') ? GrantedIcon : DeniedIcon}
                          alt=""
                        />
                        <img
                          src={item.permissions.includes('PROPOSE') ? GrantedIcon : DeniedIcon}
                          alt=""
                        />
                        <img
                          src={item.permissions.includes('VOTE') ? GrantedIcon : DeniedIcon}
                          alt=""
                        />
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
              <p className={classes.info}>{message.message}</p>
            )}
          </div>
          <div className={classes.itemWrapper}>
            <span className="text-m14 text-regular">Threshold</span>
            <span className={classes.itemContent}>
              {message.signatures.length}/{safe.threshold} confirmations
            </span>
          </div>
          <div className={classes.itemWrapper}>
            <span className="text-m14 text-regular">Initiator address</span>
            <span className={classes.itemContent}>
              <WalletWithIcons address={message.creator} />
            </span>
          </div>
          <div className={classes.itemWrapper}>
            <span className="text-m14 text-regular">Date</span>
            <span className={classes.itemContent}>{message.create_time?.toString()}</span>
          </div>
          <div className={classes.itemWrapper}>
            <span className="text-m14 text-regular">Status</span>
            <span className="text-m14 text-white flex items-center gap-8">
              <img width={20} height={20} src={isSigned ? SignedIcon : PendingIcon} alt="" />
              {isSigned ? 'Signed' : 'Pending'}
            </span>
          </div>
          {!isSigned && !message.exported && (
            <ButtonV2
              onClick={async (event) => {
                event.stopPropagation();
                onSign(message.message);
              }}
              disabled={Boolean(readOnlyWarning)}
              disableHint={readOnlyWarning}
              message="Sign"
              size="md"
              className="!py-0 w-full"
            />
          )}
        </div>
      </div>
    </TransactionCommonDialog>
  );
};
